import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertTriangle, Upload, Trash2, CheckCircle, Eye, FileSpreadsheet, X, Loader2, Download, RefreshCw, ArrowLeftRight, Shield, ClipboardPaste } from 'lucide-react';
// v3.20-1502 — Paste Client Information (hybrid regex + LLM parser).
import PasteClientInformationCard from './PasteClientInformationCard';

const CHUNK_SIZE = 200; // rows per request
const SYNC_COLLECTIONS = ['clients', 'ptc_deals', 'users', 'companies', 'deal_stages', 'payroll_commissions', 'ptc_ledger', 'communications'];

export default function DataImportPage() {
  const { user } = useAuth();
  const [pasteText, setPasteText] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companies, setCompanies] = useState([]);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testCounts, setTestCounts] = useState(null);
  const [purgeResult, setPurgeResult] = useState(null);
  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  // Chunked progress
  const [progress, setProgress] = useState(null); // { current, total, imported, skipped, errors }
  const abortRef = useRef(false);
  // Data Sync state
  const [syncProgress, setSyncProgress] = useState(null); // { phase, message, data }
  const syncFileRef = useRef(null);
  // Spreadsheet URL import state
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetCompany, setSheetCompany] = useState('');
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetResult, setSheetResult] = useState(null);
  // Spreadsheet Paste state (dedicated card)
  const [spPaste, setSpPaste] = useState('');
  const [spCompany, setSpCompany] = useState('');
  const [spLoading, setSpLoading] = useState(false);
  const [spPreview, setSpPreview] = useState(null);
  const [spResult, setSpResult] = useState(null);
  const [spProgress, setSpProgress] = useState(null);
  const spAbortRef = useRef(false);
  // v3.15-1058: Lead-staging — default ON for safety. Operator must
  // explicitly opt-OUT to expose newly imported leads to staff.
  const [importAsInactive, setImportAsInactive] = useState(true);

  const isAdmin = user?.role === 'ptc_admin' || user?.role === 'ptc_director';

  React.useEffect(() => {
    api.get('/companies').then(res => {
      setCompanies(res.data?.companies || res.data || []);
    }).catch(() => {});
  }, []);

  // Read file as text
  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  // Handle file drop/select
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'tsv', 'txt', 'tab'].includes(ext)) {
      toast.error('Supported formats: CSV, TSV, TXT');
      return;
    }
    setUploadedFile(file);
    const text = await readFile(file);
    // Convert CSV to TSV if comma-separated
    if (ext === 'csv') {
      const lines = text.split('\n');
      const converted = lines.map(line => {
        // Simple CSV→TSV: if line has commas but no tabs, split by comma
        if (line.includes('\t')) return line;
        // Handle quoted fields
        const fields = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { inQuotes = !inQuotes; }
          else if (ch === ',' && !inQuotes) { fields.push(current.trim()); current = ''; }
          else { current += ch; }
        }
        fields.push(current.trim());
        return fields.join('\t');
      }).join('\n');
      setPasteText(converted);
    } else {
      setPasteText(text);
    }
    setPreview(null);
    setImportResult(null);
    setProgress(null);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  // Preview
  const handlePreview = async () => {
    if (!pasteText.trim()) return;
    setLoading(true);
    setImportResult(null);
    setProgress(null);
    try {
      // Send only first 10 rows for preview
      const lines = pasteText.trim().split('\n');
      const previewData = lines.slice(0, 11).join('\n');
      const res = await api.post('/admin/import-paste', {
        data: previewData,
        sheet_type: 'clients',
        company_name: companyName,
        preview: true,
        import_as_inactive: importAsInactive,  // v3.15-1058
      });
      // Also count total rows
      res.data.total_rows = lines.length - 1;
      setPreview(res.data);
    } catch (err) {
      setPreview({ status: 'error', errors: [err.response?.data?.detail || err.message] });
    }
    setLoading(false);
  };

  // Chunked import for large datasets
  const handleImport = async () => {
    if (!pasteText.trim()) return;
    const lines = pasteText.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return;

    const headerLine = lines[0];
    const dataLines = lines.slice(1);
    const totalRows = dataLines.length;

    // For small datasets (< CHUNK_SIZE), send all at once
    if (totalRows <= CHUNK_SIZE) {
      setLoading(true);
      try {
        const res = await api.post('/admin/import-paste', {
          data: pasteText,
          sheet_type: 'clients',
          company_name: companyName,
          preview: false,
          import_as_inactive: importAsInactive,  // v3.15-1058
        });
        setImportResult(res.data);
        setPreview(null);
      } catch (err) {
        setImportResult({ status: 'error', errors: [err.response?.data?.detail || err.message] });
      }
      setLoading(false);
      return;
    }

    // Chunked import for large datasets
    abortRef.current = false;
    setLoading(true);
    setImportResult(null);
    setPreview(null);

    const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
    let totalImported = 0;
    let totalSkipped = 0;
    let allErrors = [];

    setProgress({ current: 0, total: totalRows, imported: 0, skipped: 0, errors: [], chunks: totalChunks, currentChunk: 0 });

    for (let i = 0; i < totalChunks; i++) {
      if (abortRef.current) {
        allErrors.push('Import cancelled by user');
        break;
      }

      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalRows);
      const chunkLines = dataLines.slice(start, end);
      const chunkData = [headerLine, ...chunkLines].join('\n');

      try {
        const res = await api.post('/admin/import-paste', {
          data: chunkData,
          sheet_type: 'clients',
          company_name: companyName,
          preview: false,
          import_as_inactive: importAsInactive,  // v3.15-1058
        });
        totalImported += res.data.imported || 0;
        totalSkipped += res.data.skipped || 0;
        if (res.data.errors?.length) allErrors.push(...res.data.errors);
      } catch (err) {
        allErrors.push(`Chunk ${i + 1}: ${err.response?.data?.detail || err.message}`);
      }

      setProgress({
        current: end,
        total: totalRows,
        imported: totalImported,
        skipped: totalSkipped,
        errors: allErrors,
        chunks: totalChunks,
        currentChunk: i + 1
      });
    }

    setImportResult({
      status: abortRef.current ? 'cancelled' : 'success',
      imported: totalImported,
      skipped: totalSkipped,
      errors: allErrors
    });
    setProgress(null);
    setLoading(false);
  };

  const cancelImport = () => { abortRef.current = true; };

  const clearFile = () => {
    setUploadedFile(null);
    setPasteText('');
    setPreview(null);
    setImportResult(null);
    setProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const loadTestCounts = async () => {
    try {
      const res = await api.get('/admin/test-data-count');
      setTestCounts(res.data.test_data_counts);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePurge = async () => {
    if (!window.confirm('Delete ALL records marked as test data? This cannot be undone.')) return;
    setLoading(true);
    try {
      const res = await api.post('/admin/purge-test-data');
      setPurgeResult(res.data);
      loadTestCounts();
    } catch (err) {
      setPurgeResult({ status: 'error', errors: [err.response?.data?.detail || err.message] });
    }
    setLoading(false);
  };

  // --- Spreadsheet URL Import ---
  const handleSheetImport = async () => {
    if (!sheetUrl.trim()) return;
    if (!sheetCompany) { toast.error('Select a company first'); return; }
    setSheetLoading(true);
    setSheetResult(null);
    try {
      const res = await api.post('/import/spreadsheet-url', {
        url: sheetUrl.trim(),
        company_name: sheetCompany,
        import_as_inactive: importAsInactive,  // v3.15-1058
      });
      setSheetResult({ status: 'success', data: res.data });
      setSheetUrl('');
    } catch (err) {
      setSheetResult({ status: 'error', message: err.response?.data?.detail || err.message });
    }
    setSheetLoading(false);
  };

  // --- Spreadsheet Paste Handlers (dedicated card) ---
  const spRowCount = spPaste.trim() ? spPaste.trim().split('\n').length - 1 : 0;

  const handleSpPreview = async () => {
    if (!spPaste.trim()) return;
    setSpLoading(true);
    setSpResult(null);
    try {
      const lines = spPaste.trim().split('\n');
      const previewData = lines.slice(0, 11).join('\n');
      const res = await api.post('/admin/import-paste', {
        data: previewData,
        sheet_type: 'clients',
        company_name: spCompany,
        preview: true,
        import_as_inactive: importAsInactive,  // v3.15-1058
      });
      res.data.total_rows = lines.length - 1;
      setSpPreview(res.data);
    } catch (err) {
      setSpPreview({ status: 'error', errors: [err.response?.data?.detail || err.message] });
    }
    setSpLoading(false);
  };

  const handleSpImport = async () => {
    if (!spPaste.trim()) return;
    if (!spCompany) { toast.error('Please select a company first'); return; }
    const lines = spPaste.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return;

    const headerLine = lines[0];
    const dataLines = lines.slice(1);
    const totalRows = dataLines.length;

    if (totalRows <= CHUNK_SIZE) {
      setSpLoading(true);
      try {
        const res = await api.post('/admin/import-paste', {
          data: spPaste,
          sheet_type: 'clients',
          company_name: spCompany,
          preview: false,
          import_as_inactive: importAsInactive,  // v3.15-1058
        });
        setSpResult(res.data);
        setSpPreview(null);
      } catch (err) {
        setSpResult({ status: 'error', errors: [err.response?.data?.detail || err.message] });
      }
      setSpLoading(false);
      return;
    }

    spAbortRef.current = false;
    setSpLoading(true);
    setSpResult(null);
    setSpPreview(null);

    const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
    let totalImported = 0;
    let totalSkipped = 0;
    let allErrors = [];
    setSpProgress({ current: 0, total: totalRows, imported: 0, skipped: 0, errors: [], chunks: totalChunks, currentChunk: 0 });

    for (let i = 0; i < totalChunks; i++) {
      if (spAbortRef.current) { allErrors.push('Import cancelled by user'); break; }
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalRows);
      const chunkLines = dataLines.slice(start, end);
      const chunkData = [headerLine, ...chunkLines].join('\n');

      try {
        const res = await api.post('/admin/import-paste', {
          data: chunkData,
          sheet_type: 'clients',
          company_name: spCompany,
          preview: false,
          import_as_inactive: importAsInactive,  // v3.15-1058
        });
        totalImported += res.data.imported || 0;
        totalSkipped += res.data.skipped || 0;
        if (res.data.errors?.length) allErrors.push(...res.data.errors);
      } catch (err) {
        allErrors.push(`Chunk ${i + 1}: ${err.response?.data?.detail || err.message}`);
      }

      setSpProgress({
        current: end, total: totalRows, imported: totalImported, skipped: totalSkipped,
        errors: allErrors, chunks: totalChunks, currentChunk: i + 1
      });
    }

    setSpResult({
      status: spAbortRef.current ? 'cancelled' : 'success',
      imported: totalImported, skipped: totalSkipped, errors: allErrors
    });
    setSpProgress(null);
    setSpLoading(false);
  };

  // --- Data Sync Handlers ---
  const handleExportSync = async () => {
    setSyncProgress({ phase: 'exporting', message: 'Exporting data...' });
    try {
      const res = await api.post('/admin/export-data', { collections: SYNC_COLLECTIONS, exclude_test: true });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ptc-sync-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const meta = res.data._meta || {};
      const collCount = Object.keys(res.data).filter(k => k !== '_meta').length;
      const totalRecords = Object.entries(res.data).filter(([k]) => k !== '_meta').reduce((sum, [, v]) => sum + (Array.isArray(v) ? v.length : 0), 0);
      setSyncProgress({ phase: 'done', message: `Exported ${totalRecords} records across ${collCount} collections`, data: meta });
    } catch (err) {
      setSyncProgress({ phase: 'error', message: err.response?.data?.detail || err.message });
    }
  };

  const handleImportSync = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSyncProgress({ phase: 'importing', message: `Reading ${file.name}...` });
    try {
      const text = await readFile(file);
      const payload = JSON.parse(text);
      if (!payload._meta) {
        setSyncProgress({ phase: 'error', message: 'Invalid sync file — missing _meta header. Use a file exported from this tool.' });
        return;
      }
      setSyncProgress({ phase: 'importing', message: `Importing data from ${payload._meta.source || 'unknown'} (exported ${payload._meta.exported_at || 'unknown'})...` });
      const res = await api.post('/admin/import-sync-data', payload);
      const results = res.data.results || {};
      const summary = Object.entries(results).map(([c, r]) => `${c}: ${r.imported} new, ${r.updated} updated, ${r.skipped} skipped`).join('\n');
      setSyncProgress({ phase: 'done', message: summary, data: results });
    } catch (err) {
      if (err instanceof SyntaxError) {
        setSyncProgress({ phase: 'error', message: 'File is not valid JSON.' });
      } else {
        setSyncProgress({ phase: 'error', message: err.response?.data?.detail || err.message });
      }
    }
    if (syncFileRef.current) syncFileRef.current.value = '';
  };

  if (!isAdmin) {
    return <div className="p-8 text-center text-red-500">Admin access required</div>;
  }

  const rowCount = pasteText.trim() ? pasteText.trim().split('\n').length - 1 : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-4" data-testid="data-import-page">
      <h1 className="text-2xl font-bold text-gray-900">Data Import & Cleanup</h1>

      {/* v3.15-1058 — Global Lead-Staging toggle (applies to ALL importers below) */}
      <Card className={`border-2 ${importAsInactive ? 'border-amber-300 bg-amber-50' : 'border-rose-300 bg-rose-50'} shadow-sm`} data-testid="lead-staging-card">
        <CardContent className="py-4 flex items-center gap-3">
          <input
            type="checkbox"
            id="import-as-inactive-toggle"
            checked={importAsInactive}
            onChange={(e) => setImportAsInactive(e.target.checked)}
            className="h-5 w-5 rounded border-2 border-slate-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
            data-testid="import-as-inactive-toggle"
          />
          <label htmlFor="import-as-inactive-toggle" className="flex-1 cursor-pointer select-none">
            <div className={`font-semibold ${importAsInactive ? 'text-amber-900' : 'text-rose-900'}`}>
              {importAsInactive
                ? 'Import as Inactive (recommended)'
                : '⚠ Import as ACTIVE — visible to reps immediately'}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {importAsInactive
                ? 'Leads will be staged in INACTIVE state — hidden from reps, dialer, queues, KPIs, Ghost, booking. Admins can bulk-activate later from the Clients page.'
                : 'Leads will be live immediately. No staging. Use only when training and rollout are complete.'}
            </div>
          </label>
        </CardContent>
      </Card>

      {/* PASTE FROM SPREADSHEET — Primary import method */}
      <Card className="border-2 border-emerald-200 shadow-md" data-testid="spreadsheet-paste-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardPaste className="w-5 h-5 text-emerald-600" />
            Paste from Spreadsheet
          </CardTitle>          <p className="text-sm text-slate-500 mt-1">
            Copy rows from Excel or Google Sheets and paste below. First row must be headers.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Company Selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
            <select
              data-testid="sp-company-select"
              className="w-full max-w-sm border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              value={spCompany}
              onChange={(e) => setSpCompany(e.target.value)}
            >
              <option value="">-- Select Company --</option>
              {companies.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Paste Area — always visible */}
          <textarea
            data-testid="sp-paste-textarea"
            className="w-full h-56 border-2 border-dashed border-slate-300 rounded-xl p-4 font-mono text-xs bg-slate-50 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 placeholder:text-slate-400 transition-colors"
            placeholder={"Paste spreadsheet data here (tab-separated)...\n\nExample:\nname\temail\tphone\tdeal_amount\tstage\nJohn Doe\tjohn@email.com\t555-1234\t25000\tfunded\nJane Smith\tjane@email.com\t555-5678\t15000\tlead"}
            value={spPaste}
            onChange={(e) => { setSpPaste(e.target.value); setSpPreview(null); setSpResult(null); }}
          />

          {/* Row count */}
          {spRowCount > 0 && (
            <div className="flex items-center gap-2 text-sm" data-testid="sp-row-count">
              <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
              <span className="font-medium text-slate-700">{spRowCount.toLocaleString()} rows detected</span>
              {spRowCount > CHUNK_SIZE && (
                <span className="text-xs text-slate-500">(will process in {Math.ceil(spRowCount / CHUNK_SIZE)} chunks)</span>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button onClick={handleSpPreview} disabled={spLoading || !spPaste.trim()} variant="outline" data-testid="sp-preview-btn">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={handleSpImport}
              disabled={spLoading || !spPaste.trim() || !spCompany}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sp-import-btn"
            >
              {spLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {spLoading ? 'Importing...' : `Import ${spRowCount > 0 ? spRowCount.toLocaleString() + ' Clients' : 'Now'}`}
            </Button>
            {spLoading && spProgress && (
              <Button onClick={() => { spAbortRef.current = true; }} variant="destructive" size="sm" data-testid="sp-cancel-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            )}
            {spPaste.trim() && (
              <Button variant="ghost" size="sm" onClick={() => { setSpPaste(''); setSpPreview(null); setSpResult(null); setSpProgress(null); }} data-testid="sp-clear-btn">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Progress bar */}
          {spProgress && (
            <div className="space-y-2 p-4 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="sp-progress">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-emerald-800">Processing chunk {spProgress.currentChunk}/{spProgress.chunks}</span>
                <span className="text-emerald-600">{spProgress.current.toLocaleString()} / {spProgress.total.toLocaleString()} rows</span>
              </div>
              <div className="w-full bg-emerald-200 rounded-full h-3">
                <div className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((spProgress.current / spProgress.total) * 100)}%` }} />
              </div>
              <div className="flex gap-4 text-xs text-emerald-700">
                <span>{spProgress.imported} imported</span>
                <span>{spProgress.skipped} skipped</span>
                {spProgress.errors.length > 0 && <span className="text-red-600">{spProgress.errors.length} errors</span>}
              </div>
            </div>
          )}

          {/* Preview */}
          {spPreview && spPreview.status === 'preview' && (
            <div className="border rounded-lg p-4 bg-emerald-50 space-y-3" data-testid="sp-preview-result">
              <p className="font-medium text-emerald-800">
                Preview: {spPreview.total_rows?.toLocaleString() || spPreview.record_count} total records
              </p>
              <p className="text-xs text-emerald-600">Headers: {spPreview.headers_detected?.join(', ')}</p>
              {spPreview.errors?.length > 0 && (
                <div className="text-xs text-red-600">
                  {spPreview.errors.map((e, i) => <div key={`sp-err-${i}-${e.slice(0,20)}`}>{e}</div>)}
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {spPreview.headers_detected?.map((h, i) => (
                        <TableHead key={h || `hdr-${i}`} className="text-xs whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {spPreview.preview?.slice(0, 5).map((row, ri) => (
                      <TableRow key={ri}>
                        {spPreview.headers_detected?.map((h, ci) => (
                          <TableCell key={ci} className="text-xs whitespace-nowrap">{row[h] ?? '-'}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {spPreview.total_rows > 5 && (
                <p className="text-xs text-slate-500">Showing first 5 of {spPreview.total_rows.toLocaleString()} rows</p>
              )}
            </div>
          )}
          {spPreview && spPreview.status === 'error' && (
            <div className="border rounded-lg p-4 bg-red-50" data-testid="sp-preview-error">
              <p className="text-red-800">{spPreview.errors?.[0] || 'Preview failed'}</p>
            </div>
          )}

          {/* Import Result */}
          {spResult && (
            <div className={`border rounded-lg p-4 ${spResult.status === 'success' ? 'bg-green-50' : spResult.status === 'cancelled' ? 'bg-yellow-50' : 'bg-red-50'}`} data-testid="sp-import-result">
              {spResult.status === 'success' ? (
                <div className="space-y-1">
                  <p className="font-medium text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Import Complete
                  </p>
                  <p className="text-sm text-green-700">
                    {spResult.imported?.toLocaleString()} imported, {spResult.skipped?.toLocaleString()} skipped (duplicates)
                  </p>
                  {spResult.errors?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-orange-600 cursor-pointer">{spResult.errors.length} warnings/errors</summary>
                      <div className="text-xs text-orange-600 mt-1 max-h-32 overflow-y-auto">
                        {spResult.errors.map((e, i) => <div key={`spr-err-${i}-${e.slice(0,20)}`}>{e}</div>)}
                      </div>
                    </details>
                  )}
                </div>
              ) : spResult.status === 'cancelled' ? (
                <p className="font-medium text-yellow-800">Import cancelled. {spResult.imported?.toLocaleString()} imported before cancellation.</p>
              ) : (
                <p className="text-red-800">{spResult.errors?.[0] || 'Import failed'}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* v3.20-1502 · Paste Client Information (regex-first + LLM fallback) */}
      <PasteClientInformationCard />

      {/* DATA SYNC - Export/Import JSON between environments */}
      <Card data-testid="data-sync-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowLeftRight className="w-5 h-5 text-indigo-600" />
            Data Sync (Preview &harr; Production)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Export all real data as JSON, then import into another environment. Duplicate records are skipped or updated based on timestamps.
          </p>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleExportSync}
              variant="outline"
              disabled={syncProgress?.phase === 'exporting'}
              data-testid="sync-export-btn"
            >
              {syncProgress?.phase === 'exporting'
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Download className="w-4 h-4 mr-2" />}
              Export JSON
            </Button>

            <div className="relative">
              <input
                type="file"
                ref={syncFileRef}
                accept=".json"
                onChange={handleImportSync}
                className="hidden"
                data-testid="sync-import-input"
              />
              <Button
                onClick={() => syncFileRef.current?.click()}
                variant="outline"
                disabled={syncProgress?.phase === 'importing'}
                data-testid="sync-import-btn"
              >
                {syncProgress?.phase === 'importing'
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Upload className="w-4 h-4 mr-2" />}
                Import JSON
              </Button>
            </div>
          </div>

          {/* Sync feedback */}
          {syncProgress && (
            <div className={`p-4 rounded-lg border text-sm ${
              syncProgress.phase === 'done' ? 'bg-green-50 border-green-200 text-green-800' :
              syncProgress.phase === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
              'bg-indigo-50 border-indigo-200 text-indigo-800'
            }`} data-testid="sync-progress">
              {syncProgress.phase === 'done' && <CheckCircle className="w-4 h-4 inline mr-2" />}
              {syncProgress.phase === 'error' && <AlertTriangle className="w-4 h-4 inline mr-2" />}
              {syncProgress.phase === 'exporting' || syncProgress.phase === 'importing'
                ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                : null}
              <span className="whitespace-pre-line">{syncProgress.message}</span>
              {syncProgress.phase === 'done' && (
                <Button variant="ghost" size="sm" className="ml-3" onClick={() => setSyncProgress(null)} data-testid="sync-dismiss-btn">
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <Shield className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
            <p className="text-xs text-indigo-800">
              Collections synced: {SYNC_COLLECTIONS.join(', ')}. Test data and merged clients are excluded from exports. Imports use ID + email deduplication.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* SPREADSHEET URL IMPORTER */}
      <Card data-testid="spreadsheet-importer-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Spreadsheet Data Uploader
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Paste a Google Sheet URL (public or shared link) to import data. The sheet must have headers in the first row.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <select
              data-testid="sheet-company-select"
              className="w-full max-w-sm border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
              value={sheetCompany}
              onChange={(e) => setSheetCompany(e.target.value)}
            >
              <option value="">-- Select Company --</option>
              {companies.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Google Sheet URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                data-testid="sheet-url-input"
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
              />
              <Button
                onClick={handleSheetImport}
                disabled={sheetLoading || !sheetUrl.trim() || !sheetCompany}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="sheet-import-btn"
              >
                {sheetLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Import Sheet
              </Button>
            </div>
          </div>
          {sheetResult && (
            <div className={`p-3 rounded-lg border text-sm ${
              sheetResult.status === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
            }`} data-testid="sheet-result">
              {sheetResult.status === 'success' ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Imported {sheetResult.data?.imported || 0} records ({sheetResult.data?.skipped || 0} skipped, {sheetResult.data?.errors || 0} errors)</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{sheetResult.message}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* MASS IMPORT */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="w-5 h-5" />
            Mass Data Importer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Upload a CSV/TSV file or paste data directly. Supports thousands of records. First row must be headers.
          </p>

          {/* Company Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <select
              data-testid="import-company-select"
              className="w-full max-w-sm border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            >
              <option value="">-- Select Company --</option>
              {companies.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* File Upload Drop Zone */}
          <div
            data-testid="file-drop-zone"
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400 bg-slate-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt,.tab"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="file-input"
            />
            <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            {uploadedFile ? (
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm font-medium text-slate-700">{uploadedFile.name}</span>
                <span className="text-xs text-slate-500">({(uploadedFile.size / 1024).toFixed(1)} KB)</span>
                <button onClick={(e) => { e.stopPropagation(); clearFile(); }} className="text-slate-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-600">Drop CSV or TSV file here</p>
                <p className="text-xs text-slate-400 mt-1">or click to browse</p>
              </>
            )}
          </div>

          {/* Row count indicator */}
          {rowCount > 0 && (
            <div className="flex items-center gap-2 text-sm" data-testid="row-count">
              <FileSpreadsheet className="w-4 h-4 text-blue-500" />
              <span className="font-medium text-slate-700">{rowCount.toLocaleString()} rows</span>
              {rowCount > CHUNK_SIZE && (
                <span className="text-xs text-slate-500">(will process in {Math.ceil(rowCount / CHUNK_SIZE)} chunks of {CHUNK_SIZE})</span>
              )}
            </div>
          )}

          {/* Paste area (collapsible for large files) */}
          <details className={uploadedFile ? '' : 'open'}>
            <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700 select-none">
              {uploadedFile ? 'View/edit raw data' : 'Or paste data directly'}
            </summary>
            <textarea
              data-testid="paste-textarea"
              className="w-full h-48 border rounded-lg p-3 font-mono text-xs bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mt-2"
              placeholder={"Paste tab-separated data here...\nname\temail\tphone\tdeal_amount\tstage\tcompany\nJohn Doe\tjohn@email.com\t555-1234\t25000\tfunded\tTradeline International"}
              value={pasteText}
              onChange={(e) => { setPasteText(e.target.value); setUploadedFile(null); }}
            />
          </details>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button onClick={handlePreview} disabled={loading || !pasteText.trim()} variant="outline" data-testid="preview-btn">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button onClick={handleImport} disabled={loading || !pasteText.trim()} data-testid="import-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {loading ? 'Importing...' : `Import ${rowCount > 0 ? rowCount.toLocaleString() + ' rows' : 'Now'}`}
            </Button>
            {loading && progress && (
              <Button onClick={cancelImport} variant="destructive" size="sm" data-testid="cancel-import-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            )}
          </div>

          {/* Progress bar for chunked import */}
          {progress && (
            <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="import-progress">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-blue-800">
                  Processing chunk {progress.currentChunk}/{progress.chunks}
                </span>
                <span className="text-blue-600">
                  {progress.current.toLocaleString()} / {progress.total.toLocaleString()} rows
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                  data-testid="progress-bar"
                />
              </div>
              <div className="flex gap-4 text-xs text-blue-700">
                <span>{progress.imported} imported</span>
                <span>{progress.skipped} skipped</span>
                {progress.errors.length > 0 && <span className="text-red-600">{progress.errors.length} errors</span>}
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && preview.status === 'preview' && (
            <div className="border rounded-lg p-4 bg-blue-50 space-y-3" data-testid="import-preview">
              <p className="font-medium text-blue-800">
                Preview: {preview.total_rows?.toLocaleString() || preview.record_count} total records
              </p>
              <p className="text-xs text-blue-600">Headers: {preview.headers_detected?.join(', ')}</p>
              {preview.errors?.length > 0 && (
                <div className="text-xs text-red-600">
                  {preview.errors.map((e, i) => <div key={`prev-err-${i}-${e.slice(0,20)}`}>{e}</div>)}
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {preview.headers_detected?.map((h, i) => (
                        <TableHead key={h || `hdr-${i}`} className="text-xs whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.preview?.slice(0, 5).map((row, i) => (
                      <TableRow key={`row-${i}-${row[preview.headers_detected?.[0]] || i}`}>
                        {preview.headers_detected?.map((h, j) => (
                          <TableCell key={j} className="text-xs whitespace-nowrap">{row[h] ?? '-'}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {preview.total_rows > 5 && (
                <p className="text-xs text-slate-500">Showing first 5 of {preview.total_rows.toLocaleString()} rows</p>
              )}
            </div>
          )}
          {preview && preview.status === 'error' && (
            <div className="border rounded-lg p-4 bg-red-50">
              <p className="text-red-800">{preview.errors?.[0] || 'Preview failed'}</p>
            </div>
          )}

          {/* Import Result — v3.18-1344 enhanced summary */}
          {importResult && (
            <div className={`border rounded-lg p-4 ${importResult.status === 'success' ? 'bg-green-50' : importResult.status === 'cancelled' ? 'bg-yellow-50' : 'bg-red-50'}`} data-testid="import-result">
              {importResult.status === 'success' ? (
                <div className="space-y-2">
                  <p className="font-medium text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Import Complete
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Imported</div>
                      <div className="font-semibold text-green-800" data-testid="summary-imported">{(importResult.imported || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Updated</div>
                      <div className="font-semibold text-slate-800" data-testid="summary-updated">{(importResult.updated || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Duplicates Skipped</div>
                      <div className="font-semibold text-slate-800" data-testid="summary-duplicates">{((importResult.duplicates ?? importResult.skipped) || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Errors</div>
                      <div className="font-semibold text-orange-700" data-testid="summary-errors">{(importResult.errors?.length || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Imported As</div>
                      <div className="font-semibold text-slate-800" data-testid="summary-imported-as">{importResult.imported_as || (importResult.imported_as_inactive ? 'inactive' : 'active')}</div>
                    </div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <details className="flex-1 min-w-[200px]">
                        <summary className="text-xs text-orange-700 cursor-pointer" data-testid="summary-view-errors">View {importResult.errors.length} error{importResult.errors.length === 1 ? '' : 's'}</summary>
                        <div className="text-xs text-orange-700 mt-1 max-h-32 overflow-y-auto bg-white/60 p-2 rounded">
                          {importResult.errors.map((e, i) => (
                            <div key={`imp-err-${i}`}>{typeof e === 'string' ? e : (e.message || JSON.stringify(e))}</div>
                          ))}
                        </div>
                      </details>
                      {importResult.batch_id && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/lead-warehouse/import-history/${importResult.batch_id}/errors.csv`}
                          className="inline-flex items-center gap-1 text-xs bg-white border border-orange-300 text-orange-700 px-2 py-1 rounded hover:bg-orange-100"
                          data-testid="summary-download-errors"
                        >
                          <Download className="w-3 h-3" /> Download Error List
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : importResult.status === 'cancelled' ? (
                <div className="space-y-1">
                  <p className="font-medium text-yellow-800">Import Cancelled</p>
                  <p className="text-sm text-yellow-700">
                    {importResult.imported?.toLocaleString()} imported before cancellation, {importResult.skipped?.toLocaleString()} skipped
                  </p>
                </div>
              ) : (
                <p className="text-red-800">{importResult.errors?.[0] || 'Import failed'}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* v3.18-1344 — Import History table */}
      <ImportHistoryCard />

      {/* TEST DATA CLEANUP */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trash2 className="w-5 h-5 text-red-500" />
            Test Data Cleanup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Only deletes records explicitly marked as test data. Real client data is never touched.
          </p>
          <div className="flex gap-3">
            <Button onClick={loadTestCounts} variant="outline" data-testid="count-test-btn">
              Count Test Records
            </Button>
            <Button onClick={handlePurge} variant="destructive" disabled={loading} data-testid="purge-test-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Purge Test Data
            </Button>
          </div>

          {testCounts && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <p className="font-medium text-gray-800 mb-2">Test records found:</p>
              {Object.entries(testCounts).map(([coll, count]) => (
                <div key={coll} className="flex justify-between text-sm py-1 border-b last:border-0">
                  <span className="text-gray-600">{coll}</span>
                  <span className={count > 0 ? 'text-orange-600 font-medium' : 'text-green-600'}>{count}</span>
                </div>
              ))}
            </div>
          )}

          {purgeResult && purgeResult.status === 'success' && (
            <div className="border rounded-lg p-4 bg-green-50">
              <p className="font-medium text-green-800 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Purge Complete
              </p>
              {Object.entries(purgeResult.deleted).map(([coll, count]) => (
                <div key={coll} className="text-sm text-green-700">{coll}: {count} deleted</div>
              ))}
            </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
            <p className="text-xs text-yellow-800">
              Detects: records marked as test, records with no name, and incomplete submissions.
              All spreadsheet imports are tagged as real data and will never be deleted.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// v3.18-1344 LEAD-WAREHOUSE-COMPLETE — Import History card.
// Renders every past import batch with Imported / Updated / Duplicates /
// Errors / Imported As, plus a Download Errors link when a batch has
// errors and a live "Import running" banner sourced from
// /lead-warehouse/import-history/active.
function ImportHistoryCard() {
  const [items, setItems]   = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: a }] = await Promise.all([
        api.get('/lead-warehouse/import-history'),
        api.get('/lead-warehouse/import-history/active'),
      ]);
      setItems(h.items || []);
      setActive(a);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => {
    load();
    const t = setInterval(load, 8_000);
    return () => clearInterval(t);
  }, [load]);
  return (
    <Card data-testid="import-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileSpreadsheet className="w-5 h-5" />
          Import History
          <Button onClick={load} variant="outline" size="sm" className="ml-auto" data-testid="import-history-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {active?.running && (
          <div className="mb-3 border border-amber-300 bg-amber-50 rounded p-2 text-sm text-amber-900 flex items-center gap-2"
               data-testid="import-history-running-banner">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>
              Import running — <b>{active.lock?.held_by?.name || 'Someone'}</b>
              {active.batch?.filename ? <> processing <code>{active.batch.filename}</code></> : null}
              {typeof active.batch?.chunks_done === 'number' && active.batch?.chunks_total > 0 && (
                <> — chunk {active.batch.chunks_done} / {active.batch.chunks_total}</>
              )}
              . New imports are disabled until it completes.
            </span>
          </div>
        )}
        {items.length === 0 && !loading && (
          <div className="text-sm text-slate-500">No prior imports on record.</div>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="import-history-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="text-left py-1 pr-2">When</th>
                  <th className="text-left py-1 pr-2">By</th>
                  <th className="text-left py-1 pr-2">Company</th>
                  <th className="text-left py-1 pr-2">Filename</th>
                  <th className="text-right py-1 pr-2">Imported</th>
                  <th className="text-right py-1 pr-2">Updated</th>
                  <th className="text-right py-1 pr-2">Duplicates</th>
                  <th className="text-right py-1 pr-2">Errors</th>
                  <th className="text-left py-1 pr-2">Imported As</th>
                  <th className="text-left py-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(row => (
                  <tr key={row.id} className="border-b border-slate-100" data-testid={`import-history-row-${row.id}`}>
                    <td className="py-1 pr-2 whitespace-nowrap">{row.started_at ? new Date(row.started_at).toLocaleString() : ''}</td>
                    <td className="py-1 pr-2">{row.imported_by?.name}</td>
                    <td className="py-1 pr-2">{row.company_name}</td>
                    <td className="py-1 pr-2"><code className="text-[11px]">{row.filename}</code></td>
                    <td className="py-1 pr-2 text-right font-semibold text-green-700">{(row.imported || 0).toLocaleString()}</td>
                    <td className="py-1 pr-2 text-right">{(row.updated || 0).toLocaleString()}</td>
                    <td className="py-1 pr-2 text-right">{(row.duplicates || 0).toLocaleString()}</td>
                    <td className="py-1 pr-2 text-right">{(row.errors?.length || 0).toLocaleString()}</td>
                    <td className="py-1 pr-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${row.imported_as === 'inactive' ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-800'}`}>
                        {row.imported_as || '—'}
                      </span>
                    </td>
                    <td className="py-1">
                      {row.errors?.length > 0 && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/lead-warehouse/import-history/${row.id}/errors.csv`}
                          className="inline-flex items-center gap-1 text-[11px] text-orange-700 hover:underline"
                          data-testid={`import-history-download-errors-${row.id}`}
                        >
                          <Download className="w-3 h-3" /> Errors
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
